import React, { useEffect, useMemo } from 'react';
import {parse as queryParse} from 'querystring';
import {useHistory, useLocation} from 'react-router';
import { useAppConfiguration } from '../../../AppConfiguration';
import {useInfiniteLocations} from '../../../api/hooks';
import {generateRoute, useRoutes} from '../../../routing/RouteProvider';
import Loader from '../Loader';
import Error from '../Error';
import {objRemoveEmptyAndFalseyProperties, isJsonParsable} from '../../../helper';
import LocationsListingHeader from './locations/LocationsListingHeader';
import LocationsListingMain from './locations/LocationsListingMain';
import setFilterOperations from '../../../util/setFilterOperations';
import { isPlaygroundsFilterSelected, usePlaygroundsFilter } from '../../../util/playgrounds';
import ScrollTopButton from '../../partials/ScrollTopButton';
import useSessionStorage from '../../../util/useSessionStorage';

const LocationsContent = () => {
    // Get filter
    const {search} = useLocation();
    const history = useHistory();

    let {locationsFilterConfiguration, fixedFilters, defaultListingsView} = useAppConfiguration();

    const {filter: filterString} = queryParse(search.substr(1));

    const filter = useMemo(() => (filterString && isJsonParsable(filterString)) ? JSON.parse(filterString) : {
        // Force POI locations
        isPOI: true,
        // Include fixed filter in the default filter
        ...setFilterOperations(fixedFilters, {
            tags: 'AND',
        }),
    }, [filterString, fixedFilters]);

    // Set filter function
    const routes = useRoutes();
    function setFilter(filter) {
        const {tags: fixedTags, categories: fixedCategories, ...fixedFilterRest} = fixedFilters;

        // Add fixed tags
        if (fixedTags && Array.isArray(fixedTags)) {
            if (!filter.tags) {
                filter.tags = fixedTags;
            } else {
                for (const tag of fixedTags) {
                    if (!filter.tags.includes(tag)) {
                        filter.tags.push(tag);
                    }
                }
            }
        }

        // Add all other fixed filters
        filter = {...filter, ...fixedFilterRest};

        // Force POI locations
        filter.isPOI = true;

        const params = {};
        if (Object.values(filter).length > 0) {
            params.filter = setFilterOperations(filter, {
                tags: 'AND',
            });
        }

        const url = generateRoute(routes, 'locations', params);

        history.push(url);
    }

    if (defaultListingsView !== 'grid' && defaultListingsView !== 'list' && defaultListingsView !== 'map') {
        defaultListingsView = 'grid';
    }
    const [listType, setListType] = useSessionStorage('locationsListingView', defaultListingsView);
    useEffect(() => {
        // Add global listing-map-view class to body, if map view is enabled. Gets used to change some styles on mobile
        // when the map view is enabled to make the page behave more app-like.
        document.body.classList.toggle('listing-map-view', listType === 'map');

        return () => document.body.classList.remove('listing-map-view');
    }, [listType]);

    // Alter the filter if we search for playgrounds (mainly remove the isPOI)
    const playgroundsFilter = usePlaygroundsFilter(filter);
    const {
        data: locationGroups,
        isLoading,
        error,
        fetchMore,
        canFetchMore,
        isFetchingMore
    } = useInfiniteLocations(isPlaygroundsFilterSelected(filter) ? playgroundsFilter : filter);

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Error errors={[error]} />;
    }

    const tags = locationsFilterConfiguration.tags || [
        'Geeignet für Familien und Kinder',
        'Barrierefrei',
    ];

    const onSubmit = newFilter => {
        // Update tags
        const tagsTitle = [];
        // Allow adding tags from the normal tag list and from the tag dropdown (if it is set)
        for (const tag of new Set([...tags, ...(locationsFilterConfiguration.tagDropdown?.tags || [])])) {
            if (newFilter[tag]) {
                tagsTitle.push(tag);
                delete newFilter[tag];
            }
        }

        if (tagsTitle.length > 0) {
            newFilter.tags = tagsTitle;
        }

        // Remove empty properties
        objRemoveEmptyAndFalseyProperties(newFilter);

        setFilter(newFilter);
    };

    return (
        <section className={`all-listing-wrapper section-bg ${listType === 'map' ? 'remove-bottom-padding' : ''}`}>
            <div className="container">
                <div className="row">
                    {/* Locations Header */}
                    <div className="col-lg-12 listing-header-container">
                        <LocationsListingHeader
                            totalCount={locationGroups[0].total_count}
                            listType={listType}
                            setListType={setListType}
                            tags={tags}
                            onSubmit={onSubmit}
                            filter={filter}
                            fixedFilter={fixedFilters}
                        />
                    </div>

                    {/* Events Listing */}
                    <div className="col-lg-12 listing-items">
                        <LocationsListingMain
                            locationGroups={locationGroups}
                            fetchMore={fetchMore}
                            canFetchMore={canFetchMore}
                            isFetchingMore={isFetchingMore}
                            tags={tags}
                            onSubmit={onSubmit}
                            listType={listType}
                            setListType={setListType}
                            filter={filter}
                            fixedFilter={fixedFilters}
                        />
                    </div>
                </div>
            </div>
            <ScrollTopButton />
        </section>
    );
};

export default LocationsContent;
